import { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { LoadPanel, SelectBox } from 'devextreme-react';
import Button from 'devextreme-react/button';
import { useTranslation } from 'react-i18next';
import dayjs from "dayjs";
import CodeMirror, { EditorView, keymap } from '@uiw/react-codemirror';
import { json } from '@codemirror/lang-json';
import { vscodeDark } from '@uiw/codemirror-theme-vscode';
import { ThemeContext } from '../../../theme/theme';
import { searchKeymap, openSearchPanel } from "@codemirror/search";
import { ReactCodeMirrorRef } from "@uiw/react-codemirror";
import { getLogsGetListDetailRequest, getLogsGetListRequest } from '../../../api/request';
import { CustomDataGrid } from '../CustomDataGrid';

const stripNull = (s: string) => s.replace(new RegExp("\\u0000", "g"), "");

const formatDate = (d: Date) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const PRODUCT_OPTIONS = [
  { value: 10, label: 'mobiOffice' },
  { value: 11, label: 'mobiSales' },
  { value: 12, label: 'mobiShop' },
  { value: 13, label: 'mobiAdmin' },
];

export const ManagementLogs = () => {
  const { t } = useTranslation();
  const themeContext = useContext(ThemeContext);

  // List state
  const [logList, setLogList] = useState([]);
  const [loading, setLoading] = useState(true);

  // Detail (JSON) state
  const [selectedLogRef, setSelectedLogRef] = useState<string | null>(null);
  const [requestJson, setRequestJson] = useState<string>('');
  const [responseJson, setResponseJson] = useState<string>('');
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const editorRef = useRef<ReactCodeMirrorRef>(null);
  const editorRef2 = useRef<ReactCodeMirrorRef>(null);

  // Filter state
  const [dateFrom, setDateFrom] = useState<Date | null>(new Date());
  const [dateTo, setDateTo] = useState<Date | null>(new Date());
  const [productCode, setProductCode] = useState<number>(10);
  const resCode = 0;

  // ── Layout (duz CSS splitter; DevExtreme Splitter degil) ───────────────────
  // screenStatus: 0 = split, 1 = grid tam ekran, 2 = JSON tam ekran
  const [screenStatus, setScreenStatus] = useState<number>(0);
  const [topHeight, setTopHeight] = useState<number | string>('50%');
  const containerRef = useRef<HTMLDivElement>(null);
  const bottomPanelRef = useRef<HTMLDivElement>(null);
  const [leftJsonWidth, setLeftJsonWidth] = useState<number>(50);
  const [topJsonHeight, setTopJsonHeight] = useState<number>(50);
  const [isLargeScreen, setIsLargeScreen] = useState<boolean>(window.innerWidth >= 1024);

  const splitterHeight = 4;
  const minBottomPanelHeight = 100;

  // Ana yatay bolucu (grid <-> JSON)
  const handleMainHorizontalDrag = (e: MouseEvent) => {
    if (!containerRef.current) return;
    const containerTop = containerRef.current.getBoundingClientRect().top;
    const containerHeight = containerRef.current.clientHeight;
    const newHeight = e.clientY - containerTop;
    const minHeight = 200;
    const maxHeight = containerHeight - minBottomPanelHeight - splitterHeight;
    if (newHeight < minHeight) setTopHeight(minHeight);
    else if (newHeight > maxHeight) setTopHeight(maxHeight);
    else setTopHeight(newHeight);
  };

  const onMainHorizontalMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    document.addEventListener("mousemove", handleMainHorizontalDrag);
    document.addEventListener("mouseup", () => {
      document.removeEventListener("mousemove", handleMainHorizontalDrag);
    }, { once: true });
  };

  // JSON panelleri arasi bolucu (responsive)
  const handleJsonVerticalDrag = (e: MouseEvent) => {
    if (!bottomPanelRef.current || !isLargeScreen) return;
    const rect = bottomPanelRef.current.getBoundingClientRect();
    const newWidth = ((e.clientX - rect.left) / rect.width) * 100;
    setLeftJsonWidth(Math.min(90, Math.max(10, newWidth)));
  };

  const handleJsonHorizontalDrag = (e: MouseEvent) => {
    if (!bottomPanelRef.current || isLargeScreen) return;
    const rect = bottomPanelRef.current.getBoundingClientRect();
    const newHeight = ((e.clientY - rect.top) / rect.height) * 100;
    setTopJsonHeight(Math.min(90, Math.max(10, newHeight)));
  };

  const onJsonVerticalMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    document.addEventListener("mousemove", handleJsonVerticalDrag);
    document.addEventListener("mouseup", () => {
      document.removeEventListener("mousemove", handleJsonVerticalDrag);
    }, { once: true });
  };

  const onJsonHorizontalMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    document.addEventListener("mousemove", handleJsonHorizontalDrag);
    document.addEventListener("mouseup", () => {
      document.removeEventListener("mousemove", handleJsonHorizontalDrag);
    }, { once: true });
  };

  useEffect(() => {
    const handleResize = () => setIsLargeScreen(window.innerWidth >= 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ── Load log list ─────────────────────────────────────────────────────────
  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const beginDate = formatDate(dateFrom || new Date());
      const endDate = formatDate(dateTo || new Date());
      const response = await getLogsGetListRequest(beginDate, endDate, resCode, productCode);

      if (response?.resCode === 200 && response.dataSet) {
        setLogList(response.dataSet);
      } else {
        setLogList([]);
        console.error("API Hatası:", response?.resMessage || "Error..");
      }
    } catch (error) {
      setLogList([]);
      console.error("Veri çekme hatası:", error);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, productCode]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  // ── Load request/response detail for the selected log ──────────────────────
  const loadLogDetail = useCallback(async (logRef?: string) => {
    if (!logRef || logRef === selectedLogRef) return;
    setSelectedLogRef(logRef);
    setIsLoadingDetail(true);
    setRequestJson('');
    setResponseJson('');

    try {
      const response = await getLogsGetListDetailRequest(logRef);
      if (response?.resCode === 200 && response.dataSet && response.dataSet.length > 0) {
        const data = response.dataSet[0];
        setRequestJson(data.requestJson || '');
        setResponseJson(data.responseJson || '');
      } else {
        setRequestJson('Error loading request data');
        setResponseJson('Error loading response data');
      }
    } catch (error) {
      console.error("Detail veri çekme hatası:", error);
      setRequestJson('Error loading request data');
      setResponseJson('Error loading response data');
    } finally {
      setIsLoadingDetail(false);
    }
  }, [selectedLogRef]);

  const handleRowClick = (data: any) => loadLogDetail(data?.logRef);
  const handleFocusedRowChanged = useCallback((e: any) => {
    loadLogDetail(e?.row?.data?.logRef);
  }, [loadLogDetail]);

  // Liste yuklendiginde, mevcut secim listede yoksa ilk satiri sec ve detayini ac
  useEffect(() => {
    if (logList.length === 0) return;
    const stillSelected = selectedLogRef && logList.some((d: any) => d.logRef === selectedLogRef);
    if (!stillSelected) {
      loadLogDetail((logList[0] as any).logRef);
    }
  }, [logList, selectedLogRef, loadLogDetail]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const formatJson = (jsonString: string) => {
    if (!jsonString) return '';
    try {
      const cleaned = stripNull(jsonString);
      return JSON.stringify(JSON.parse(cleaned), null, 2);
    } catch {
      return stripNull(jsonString);
    }
  };

  const handleCopy = (text: string) => {
    if (!text) return;
    navigator.clipboard.writeText(formatJson(text));
  };

  const dateTimeCellRender = (cell: any) =>
    cell.value ? dayjs(cell.value).format("DD.MM.YYYY HH:mm:ss") : '';

  const columns = [
    { dataField: 'vendorId', caption: 'Vendor ID', alignment: 'left' as const, width: 140 },
    { dataField: 'vendorName', caption: t("vendorName"), width: 140 },
    { dataField: 'serviceId', caption: 'Service ID', alignment: 'left' as const, width: 140 },
    { dataField: 'serviceName', caption: t("serviceName"), width: 140 },
    { dataField: 'productCode', caption: t("productCode"), alignment: 'left' as const, width: 140 },
    { dataField: 'productName', caption: t("productName"), width: 140 },
    { dataField: 'requestDate', caption: t("requestDate"), dataType: 'datetime' as const, cellRender: dateTimeCellRender, width: 140 },
    { dataField: 'responseDate', caption: t("responseDate"), dataType: 'datetime' as const, cellRender: dateTimeCellRender, width: 140 },
    { dataField: 'resCode', caption: t("resCode"), alignment: 'left' as const, width: 140 },
    { dataField: 'resMessage', caption: t("resMessage"), width: 140 },
    { dataField: '', caption: '', width: undefined, allowSorting: false, allowFiltering: false },
  ];

  // ── JSON panel ──────────────────────────────────────────────────────────────
  const renderJsonPanel = (
    title: string,
    value: string,
    editor: React.RefObject<ReactCodeMirrorRef>,
    headerExtra?: React.ReactNode,
  ) => (
    <div className="h-full flex flex-col overflow-hidden" style={{ padding: '8px 12px' }}>
      <div style={{
        fontWeight: 'bold',
        marginBottom: '8px',
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <span>{title}</span>
        <div style={{ display: 'flex', gap: '5px' }}>
          <Button
            icon="search"
            stylingMode="text"
            onClick={() => {
              const view = editor.current?.view;
              if (view) openSearchPanel(view);
            }}
          />
          <Button
            icon="copy"
            stylingMode="text"
            onClick={() => handleCopy(value)}
            disabled={!selectedLogRef || !value}
          />
          {headerExtra}
        </div>
      </div>
      <div style={{
        flex: 1,
        border: '1px solid #ddd',
        borderRadius: '4px',
        overflow: 'auto',
        minHeight: 0,
      }}>
        {isLoadingDetail ? (
          <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>
        ) : (
          <CodeMirror
            ref={editor}
            value={selectedLogRef ? formatJson(value) : ''}
            extensions={[
              json(),
              EditorView.editable.of(false),
              EditorView.lineWrapping,
              keymap.of(searchKeymap),
              EditorView.theme({ '&': { fontSize: '11px' } }),
            ]}
            theme={themeContext?.theme === 'dark' ? vscodeDark : undefined}
            className="border rounded-md h-full"
            style={{ color: themeContext?.theme === 'dark' ? "white" : "black" }}
            height="100%"
            basicSetup={{
              lineNumbers: true,
              foldGutter: true,
              dropCursor: false,
              allowMultipleSelections: false,
              indentOnInput: true,
              bracketMatching: true,
              closeBrackets: true,
              autocompletion: false,
              highlightSelectionMatches: false,
            }}
          />
        )}
      </div>
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div
      ref={containerRef}
      className="logs-page-root"
      style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', minHeight: 0, overflow: 'hidden' }}
    >
      <LoadPanel visible={loading} />

      {/* Üst Panel - Grid */}
      <div style={{
        height: screenStatus === 1 ? '100%' : screenStatus === 2 ? '0px' : topHeight,
        minHeight: screenStatus === 2 ? 0 : 200,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
        <CustomDataGrid
          className="custom-grid compact-grid"
          data={logList}
          columns={columns}
          keyExpr="logRef"
          height="100%"
          t={t}
          noDataText={t("noData")}
          selectionEnabled
          rowAlternationEnabled={false}
          onRefresh={loadLogs}
          onRowClick={handleRowClick}
          onFocusedRowChanged={handleFocusedRowChanged}
          focusedRowKey={selectedLogRef ?? undefined}
          autoFocus
          dateFilters={[
            { value: dateFrom, onValueChanged: setDateFrom, displayFormat: 'dd.MM.yyyy', width: 150 },
            { value: dateTo, onValueChanged: setDateTo, displayFormat: 'dd.MM.yyyy', width: 150 },
          ]}
          additionalToolbarItems={[
            {
              location: 'before',
              render: () => (
                <SelectBox
                  dataSource={PRODUCT_OPTIONS}
                  value={productCode}
                  valueExpr="value"
                  displayExpr="label"
                  onValueChanged={e => setProductCode(e.value)}
                  width={130}
                  placeholder="productCode"
                />
              ),
            },
            {
              location: 'after',
              render: () => (
                <Button
                  icon={screenStatus === 1 ? "chevronup" : "chevrondown"}
                  hint={t("expandTable")}
                  stylingMode="text"
                  onClick={() => setScreenStatus(screenStatus === 1 ? 0 : 1)}
                />
              ),
            },
          ]}
        />
      </div>

      {/* Ana yatay bölücü (sürüklenebilir) */}
      {screenStatus === 0 && (
        <div
          style={{ height: splitterHeight, cursor: "row-resize", background: "#ddd", zIndex: 2, userSelect: "none", width: "100%" }}
          onMouseDown={onMainHorizontalMouseDown}
        />
      )}

      {/* Alt Panel - Request / Response JSON */}
      <div
        ref={bottomPanelRef}
        style={{
          height: screenStatus === 2 ? '100%' : screenStatus === 1 ? '0px' : "auto",
          flex: screenStatus === 1 ? 0 : 1,
          minHeight: screenStatus === 1 ? 0 : 100,
          backgroundColor: 'var(--base-bg)',
          display: 'flex',
          flexDirection: isLargeScreen ? 'row' : 'column',
          overflow: 'hidden',
        }}
      >
        {/* Sol/Üst - Request */}
        <div style={{
          width: isLargeScreen ? `${leftJsonWidth}%` : '100%',
          height: isLargeScreen ? '100%' : `${topJsonHeight}%`,
          minWidth: isLargeScreen ? 100 : 'auto',
          minHeight: 100,
          borderRight: isLargeScreen ? '1px solid #ddd' : 'none',
          borderBottom: isLargeScreen ? 'none' : '1px solid #ddd',
        }}>
          {renderJsonPanel(t("requestJSON"), requestJson, editorRef)}
        </div>

        {/* JSON panelleri arası bölücü */}
        <div
          style={{
            width: isLargeScreen ? 2 : '100%',
            height: isLargeScreen ? '100%' : 2,
            cursor: isLargeScreen ? "col-resize" : "row-resize",
            background: "#ddd",
            zIndex: 2,
            userSelect: "none",
            flexShrink: 0,
          }}
          onMouseDown={isLargeScreen ? onJsonVerticalMouseDown : onJsonHorizontalMouseDown}
        />

        {/* Sağ/Alt - Response */}
        <div style={{
          width: isLargeScreen ? `${100 - leftJsonWidth}%` : '100%',
          height: isLargeScreen ? '100%' : `${100 - topJsonHeight}%`,
          minWidth: isLargeScreen ? 100 : 'auto',
          minHeight: 100,
        }}>
          {renderJsonPanel(
            t("responseJSON"),
            responseJson,
            editorRef2,
            <Button
              icon={screenStatus === 2 ? "chevrondown" : "chevronup"}
              hint={t("expandTable")}
              stylingMode="text"
              onClick={() => setScreenStatus(screenStatus === 2 ? 0 : 2)}
            />,
          )}
        </div>
      </div>
    </div>
  );
};
